/*
 * Copyright Mimic Networks, Inc. 2026.
 */

import { useMutation, useQueryClient } from '@tanstack/react-query';

import { ArchiveNodeResponse, ApiError } from '@/client';
import { mgmtPlaneAPIClient } from '@/client/mgmtPlane';
import { useMgmtPlaneStateStore } from '@/state/mgmtPlaneStore';

export type ArchiveNodeRequest = {
  nodeId: string;
};

export type ArchiveNodeMutationConfig = {
  onSuccess?: (data: ArchiveNodeResponse, variables: ArchiveNodeRequest) => void;
  onError: (error: unknown, variables: ArchiveNodeRequest) => void;
  onSettled?: (data: unknown, error: unknown, variables: unknown) => void;
  onMutate?: (variables: ArchiveNodeRequest) => void;
};

/**
 * Hook for archiving a node by ID through the management plane API.
 *
 * @param config.onSuccess - Called after successful archiving of a node
 * @param config.onError - Called if archiving fails
 * @param config.onSettled - Called after mutation completes (success or failure)
 *
 * @returns React Query mutation result for archiving a node by ID
 *
 * @example
 * ```tsx
 * const { mutate } = useArchiveNodeMutation({
 *   onSuccess: (data) => console.log('Node archived:', data),
 *   onError: (error) => console.error('Archiving node failed:', error)
 * });
 *
 * // Archive node
 * mutate({ nodeId: "123" });
 * ```
 */

export function useArchiveNodeMutation({ onSuccess, onError, onSettled, onMutate }: ArchiveNodeMutationConfig) {
  const tenantId = useMgmtPlaneStateStore((state) => state.selectedTenantID);
  const queryClient = useQueryClient();

  const { mutate, isPending, isError, error } = useMutation<ArchiveNodeResponse, ApiError, ArchiveNodeRequest>({
    mutationFn: ({ nodeId }) => {
      return mgmtPlaneAPIClient.nodes.archiveNode({
        tenantId: tenantId!,
        id: nodeId,
      });
    },
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['node', tenantId, variables.nodeId] });
      onSuccess?.(data, variables);
    },
    onError,
    onSettled,
    onMutate,
  });

  return { mutate, isPending, isError, error };
}
